// Log outcome for a call from a known patient (CU-869fn2wbb, CU-869fn2wbq, CU-869fn2wce).
//
// Opens at once with a loading state (it used to show nothing until the patient had loaded and
// the popup's own wait had passed), loads the patient — through the archived read when the
// patient lookup hides them — and then follows their stage (stageOutcomeModel):
//   enquiry      → the existing contact popup, handed the patient it just loaded;
//   later stages → a short choice that records how the call-back went, and nothing else;
//   not loaded   → says so, with Try again and Open patient (it used to close without a word).
import PropTypes from 'prop-types';
import { Suspense, useCallback, useEffect, useRef, useState } from 'react';
import { Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Stack, Typography } from '@mui/material';
import { callApi } from '../../utils/apiCall';
import { lazyWithRetries } from '../../routes/lazyRetry';
import { outcomeBlockedReason, outcomeOptionsFor } from '../../pages/dashboard/callLogs/rowActions';
import { callLayer } from '../callPill/callLayers';
import FlowBoundary from './FlowBoundary';
import { LEAD_STAGE, archivedLeadUrl, leadDetailUrl, leadStageOf, stageCopy, stageOutcomeChoices, usesEnquiryPopup } from './stageOutcomeModel';

// Loaded on first use (the follow-up step pulls in a large part of the lead screens), with
// retries and inside FlowBoundary: a stale chunk after a deploy must not blank the app.
const LeadFollowUpLauncher = lazyWithRetries(() => import('../leadFollowUp/LeadFollowUpLauncher'));

const READ_OPTIONS = { suppressServerErrorDispatch: true };

// { lead } or { failed: true }. A patient the row already knows is archived is read there first.
export async function loadCallLead(practiceId, leadId, { archived = false } = {}) {
    const urls = archived
        ? [archivedLeadUrl(practiceId, leadId), leadDetailUrl(practiceId, leadId)]
        : [leadDetailUrl(practiceId, leadId), archivedLeadUrl(practiceId, leadId)];
    const first = await callApi(urls[0], 'GET', null, false, READ_OPTIONS).catch(() => null);
    if (first?.status === 200 && first.data) return { lead: first.data };
    // Only "not found here" means "look in the other place"; anything else is a failed load.
    if (first?.status !== 404) return { failed: true };
    const second = await callApi(urls[1], 'GET', null, false, READ_OPTIONS).catch(() => null);
    if (second?.status === 200 && second.data) return { lead: second.data };
    return { failed: true };
}

const PHASE = { LOADING: 'loading', ENQUIRY: 'enquiry', CHOICE: 'choice', FAILED: 'failed' };

export default function CallOutcomeScreen({
    practiceId, call, initialCard, hideNoAnswer, onChoose, onLeadStepComplete, onClose, onOpenPatient, onNeedsLeadPage,
}) {
    const [phase, setPhase] = useState(PHASE.LOADING);
    const [lead, setLead] = useState(null);
    const [launcherOpen, setLauncherOpen] = useState(false);
    const [saving, setSaving] = useState(null);
    // A save that may have gone through (no reply): the choices stay locked.
    const [uncertain, setUncertain] = useState(false);
    const [attempt, setAttempt] = useState(0);
    const mounted = useRef(true);
    useEffect(() => () => { mounted.current = false; }, []);

    const leadId = call.lead?.id;
    const name = call.lead?.name || '';

    useEffect(() => {
        let active = true;
        setPhase(PHASE.LOADING);
        loadCallLead(practiceId, leadId, { archived: call.lead?.isArchived === true }).then((result) => {
            if (!active) return;
            if (result.failed) { setPhase(PHASE.FAILED); return; }
            setLead(result.lead);
            setPhase(usesEnquiryPopup(leadStageOf(result.lead)) ? PHASE.ENQUIRY : PHASE.CHOICE);
        });
        return () => { active = false; };
    }, [practiceId, leadId, call.lead?.isArchived, attempt]);

    const choose = useCallback(async (key) => {
        if (saving || uncertain) return;
        setSaving(key);
        const result = await onChoose(key);
        if (!mounted.current) return;
        setSaving(null);
        if (result?.uncertain) setUncertain(true);
    }, [onChoose, saving, uncertain]);

    const stage = lead ? leadStageOf(lead) : null;
    const blocked = outcomeBlockedReason(call.row);
    const choices = stageOutcomeChoices({ serverOptions: outcomeOptionsFor(call.row), answered: hideNoAnswer });
    const copy = stage ? stageCopy(stage, lead, name) : null;
    const showShell = phase !== PHASE.ENQUIRY || !launcherOpen;

    let body;
    if (phase === PHASE.LOADING || phase === PHASE.ENQUIRY) {
        body = (
            <Stack direction="row" spacing={1.5} alignItems="center" sx={{ py: 2 }} data-testid="call-outcome-loading">
                <CircularProgress size={22} />
                <Typography variant="body2" color="text.secondary">{name ? `Loading ${name}'s details…` : 'Loading the patient…'}</Typography>
            </Stack>
        );
    } else if (phase === PHASE.FAILED) {
        body = (
            <Typography variant="body2" sx={{ py: 1 }} data-testid="call-outcome-failed">
                Couldn&apos;t load {name || 'this patient'}. Check your connection and try again, or open the patient to log the call there.
            </Typography>
        );
    } else {
        body = (
            <Box data-testid="call-outcome-choice" data-stage={stage}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>{copy.heading}</Typography>
                {copy.detail && <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>{copy.detail}</Typography>}
                {blocked ? (
                    <Typography variant="body2" sx={{ mt: 2 }}>{blocked}</Typography>
                ) : (
                    <>
                        <Typography variant="overline" color="text.secondary" sx={{ display: 'block', mt: 2 }}>How did the call-back go?</Typography>
                        <Stack direction="row" spacing={1} sx={{ mt: 0.5, flexWrap: 'wrap', rowGap: 1 }}>
                            {choices.map((c) => (
                                <Button
                                    key={c.key}
                                    variant="outlined"
                                    disabled={Boolean(saving) || uncertain}
                                    onClick={() => choose(c.key)}
                                    startIcon={saving === c.key ? <CircularProgress size={14} /> : null}
                                    sx={{ textTransform: 'none !important' }}
                                >
                                    {c.label}
                                </Button>
                            ))}
                        </Stack>
                        {uncertain && (
                            <Typography variant="body2" color="warning.main" sx={{ mt: 1.5 }}>
                                Couldn&apos;t confirm the save. Refresh the list to check before trying again.
                            </Typography>
                        )}
                    </>
                )}
            </Box>
        );
    }

    return (
        <>
            {phase === PHASE.ENQUIRY && (
                // The existing contact popup, handed the patient loaded above so it doesn't load them again.
                <FlowBoundary onFail={onClose}>
                    <Suspense fallback={null}>
                        <LeadFollowUpLauncher
                            practiceId={practiceId}
                            leadId={leadId}
                            prefetched={Promise.resolve({ lead })}
                            source={`call-flow:outcome(${call.outcome || 'unknown'})`}
                            hideDebug
                            forceFollowUp
                            initialCard={initialCard}
                            hideNoAnswer={hideNoAnswer}
                            onOpened={() => setLauncherOpen(true)}
                            onComplete={onLeadStepComplete}
                            onClose={onClose}
                            onNeedsLeadPage={onNeedsLeadPage}
                        />
                    </Suspense>
                </FlowBoundary>
            )}
            <Dialog
                open={showShell}
                onClose={saving ? undefined : onClose}
                maxWidth="xs"
                fullWidth
                aria-labelledby="call-outcome-title"
                sx={{ zIndex: callLayer('DIALOG') }}
                data-testid="call-outcome-screen"
            >
                <DialogTitle id="call-outcome-title">Log outcome{name ? ` · ${name}` : ''}</DialogTitle>
                <DialogContent>{body}</DialogContent>
                <DialogActions sx={{ '& .MuiButton-root': { textTransform: 'none !important' } }}>
                    {(phase === PHASE.CHOICE || phase === PHASE.FAILED) && (
                        <Button onClick={onOpenPatient} disabled={Boolean(saving)} sx={{ mr: 'auto' }}>Open patient</Button>
                    )}
                    {phase === PHASE.FAILED && <Button onClick={() => setAttempt((n) => n + 1)}>Try again</Button>}
                    <Button onClick={onClose} disabled={Boolean(saving)}>{uncertain ? 'Close' : 'Cancel'}</Button>
                </DialogActions>
            </Dialog>
        </>
    );
}

CallOutcomeScreen.propTypes = {
    practiceId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
    // A flow call (callFlowModel.normaliseCall) with a lead.
    call: PropTypes.object.isRequired,
    initialCard: PropTypes.string,
    // The call ended answered: no No answer / Left voicemail.
    hideNoAnswer: PropTypes.bool,
    // (outcomeKey) → Promise<{ ok, uncertain? }>: records the short choice; the host closes on ok.
    onChoose: PropTypes.func.isRequired,
    // The contact popup saved a card ({ outcome, via }).
    onLeadStepComplete: PropTypes.func.isRequired,
    onClose: PropTypes.func.isRequired,
    onOpenPatient: PropTypes.func.isRequired,
    onNeedsLeadPage: PropTypes.func,
};
CallOutcomeScreen.defaultProps = { initialCard: null, hideNoAnswer: false, onNeedsLeadPage: undefined };

export { LEAD_STAGE };
